import { useState, useRef, useMemo } from 'react'
import { Upload, FolderOpen, ChevronRight, CheckCheck, Video, Sparkles, Loader2, CalendarDays, Clock, Info, ChevronDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import {
  useUploadYouTubeVideo, useUploadYouTubeVideoFromUrl, useYouTubeVideos,
  useScheduleYouTubeVideos, useYouTubeTagPresets,
} from '@/api/hooks/useYouTube'
import { useMediaFolders } from '@/api/hooks/useMediaLibrary'
import { useOpenAIStatus, useGenerateYtMeta } from '@/api/hooks/useOpenAI'
import { FolderTreePicker } from '@/components/FolderTreePicker'

// ── SEO Score ──

const STOPWORDS = new Set([
  'com', 'para', 'por', 'que', 'uma', 'uns', 'dos', 'das', 'nos', 'nas',
  'seu', 'sua', 'esse', 'essa', 'este', 'esta', 'como', 'mais', 'muito',
  'bem', 'the', 'and', 'for', 'you', 'are', 'not', 'but', 'can', 'has',
  'was', 'all', 'her', 'his', 'had', 'ela', 'ele', 'eles', 'elas', 'isso',
  'aqui', 'ali', 'onde', 'quando', 'tambem', 'ainda', 'ja', 'nem', 'ou',
  'se', 'de', 'da', 'do', 'no', 'na', 'em', 'um', 'ao', 'os', 'as',
])

function getTitleKeywords(title: string): string[] {
  return title
    .toLowerCase()
    .replace(/[^a-záàâãéêíóôõúç0-9\s]/g, '')
    .split(/\s+/)
    .filter(w => w.length > 3 && !STOPWORDS.has(w))
}

interface SeoBreakdown {
  title: { score: number; max: number; tips: string[] }
  description: { score: number; max: number; tips: string[] }
  tags: { score: number; max: number; tips: string[] }
  total: number
}

function calcSeoScore(title: string, description: string, tags: string): SeoBreakdown {
  const t = title.trim()
  const d = description.trim()
  const dLower = d.toLowerCase()
  const tagList = tags.split(',').map(s => s.trim()).filter(Boolean)
  const titleKeywords = getTitleKeywords(t)

  const titleTips: string[] = []
  let titleScore = 0
  if (t.length > 0) titleScore += 5
  else titleTips.push('Adicione um titulo')
  if (t.length >= 40 && t.length <= 60) titleScore += 10
  else if (t.length > 0) {
    if (t.length < 40) titleTips.push(`Titulo curto: ${t.length} chars (ideal: 40-60)`)
    else if (t.length <= 70) titleScore += 7
    else titleTips.push(`Titulo longo: ${t.length} chars (ideal: 40-60, max 70)`)
  }
  if (titleKeywords.length >= 3) titleScore += 8
  else if (t.length > 0) titleTips.push('Use 3+ palavras-chave no titulo')
  const powerWords = /[?!]|segredo|incrivel|voce|descubra|top|melhor|pior|nunca|sempre|gratis|facil|rapido|novo|verdade|por\s?que|real|chocante|surpreend/i
  if (powerWords.test(t)) titleScore += 7
  else if (t.length > 0) titleTips.push('Use palavras de impacto (?, !, descubra, incrivel...)')
  if (/\d/.test(t)) titleScore += 5
  else if (t.length > 0) titleTips.push('Numeros no titulo aumentam CTR')
  if (t === t.toUpperCase() && t.length > 3) titleTips.push('Evite TUDO MAIUSCULO')
  else if (t.length > 0) titleScore += 3
  if (t.length > 0 && (t.length < 20 || t.length > 70)) titleScore = Math.max(0, titleScore - 3)

  const descTips: string[] = []
  let descScore = 0
  if (d.length > 0) descScore += 3
  else descTips.push('Adicione uma descricao')
  if (d.length >= 150) descScore += 5
  else if (d.length > 0) descTips.push(`Descricao: ${d.length} chars (minimo 150 para aparecer no fold)`)
  if (d.length >= 500) descScore += 5
  else if (d.length >= 150) descTips.push(`Descricao: ${d.length} chars (ideal: 500+)`)
  if (d.length >= 1000) descScore += 4
  if (d.includes('\n')) descScore += 3
  else if (d.length > 50) descTips.push('Quebre em paragrafos para melhor leitura')
  const hashCount = (d.match(/#\w/g) || []).length
  if (hashCount >= 3 && hashCount <= 15) descScore += 3
  else if (hashCount > 15) descTips.push('Max 15 hashtags (YouTube ignora todas se >15)')
  else if (d.length > 0) descTips.push('Adicione 3-5 #hashtags na descricao')
  const ctaWords = /inscreva|siga|curta|compartilh|coment|link|deixe|ative|clique|acesse/i
  if (ctaWords.test(d)) descScore += 4
  else if (d.length > 0) descTips.push('Adicione CTA: inscreva-se, curta, compartilhe...')
  if (/https?:\/\//.test(d)) descScore += 3
  else if (d.length > 0) descTips.push('Inclua links relevantes na descricao')
  const hasTripleDesc = titleKeywords.length > 0 && titleKeywords.some(kw => dLower.includes(kw))
  if (hasTripleDesc) descScore += 5
  else if (t.length > 0 && d.length > 0) descTips.push('Repita keyword do titulo na descricao (Triple Keyword)')

  const tagsTips: string[] = []
  let tagsScore = 0
  const totalTagChars = tagList.reduce((s, tg) => s + tg.length, 0)
  if (tagList.length > 0) tagsScore += 3
  else tagsTips.push('Adicione tags para SEO')
  if (tagList.length >= 5) tagsScore += 5
  else if (tagList.length > 0) tagsTips.push(`${tagList.length} tag(s) — adicione pelo menos 5`)
  if (tagList.length >= 8 && tagList.length <= 12) tagsScore += 4
  else if (tagList.length >= 5) tagsTips.push(`${tagList.length} tags (ideal: 8-12)`)
  if (totalTagChars >= 200 && totalTagChars <= 500) tagsScore += 4
  else if (tagList.length > 0) tagsTips.push(`Tags: ${totalTagChars} chars (ideal: 200-300)`)
  const hasShort = tagList.some(tg => tg.split(' ').length === 1)
  const hasLong = tagList.some(tg => tg.split(' ').length >= 2)
  if (hasShort && hasLong) tagsScore += 4
  else if (tagList.length > 0) tagsTips.push('Misture tags curtas e long-tail (2+ palavras)')
  const tagsLower = tagList.map(tg => tg.toLowerCase())
  const hasTripleTags = titleKeywords.length > 0 && titleKeywords.some(kw => tagsLower.some(tg => tg.includes(kw)))
  if (hasTripleTags) tagsScore += 5
  else if (t.length > 0 && tagList.length > 0) tagsTips.push('Repita keyword do titulo nas tags (Triple Keyword)')

  titleScore = Math.min(40, Math.max(0, titleScore))
  descScore = Math.min(35, Math.max(0, descScore))
  tagsScore = Math.min(25, Math.max(0, tagsScore))
  const total = titleScore + descScore + tagsScore

  return {
    title: { score: titleScore, max: 40, tips: titleTips },
    description: { score: descScore, max: 35, tips: descTips },
    tags: { score: tagsScore, max: 25, tips: tagsTips },
    total,
  }
}

function scoreColor(score: number): string {
  if (score >= 80) return 'text-emerald-500'
  if (score >= 60) return 'text-emerald-400'
  if (score >= 40) return 'text-yellow-500'
  if (score >= 20) return 'text-orange-500'
  return 'text-red-500'
}

function scoreBg(score: number): string {
  if (score >= 80) return 'bg-emerald-500'
  if (score >= 60) return 'bg-emerald-400'
  if (score >= 40) return 'bg-yellow-500'
  if (score >= 20) return 'bg-orange-500'
  return 'bg-red-500'
}

function scoreLabel(score: number): string {
  if (score >= 80) return 'Excelente'
  if (score >= 60) return 'Bom'
  if (score >= 40) return 'Regular'
  if (score >= 20) return 'Fraco'
  return 'Ruim'
}

function SeoScoreCard({ title, description, tags }: { title: string; description: string; tags: string }) {
  const seo = useMemo(() => calcSeoScore(title, description, tags), [title, description, tags])
  if (!title.trim() && !description.trim() && !tags.trim()) return null

  const categories = [
    { label: 'Titulo', ...seo.title },
    { label: 'Descricao', ...seo.description },
    { label: 'Tags', ...seo.tags },
  ]
  const allTips = categories.flatMap(c => c.tips)

  return (
    <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-2.5">
      <div className="flex items-center gap-3">
        <div className="relative flex h-12 w-12 shrink-0 items-center justify-center">
          <svg className="h-12 w-12 -rotate-90" viewBox="0 0 36 36">
            <circle cx="18" cy="18" r="15.5" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-border" />
            <circle cx="18" cy="18" r="15.5" fill="none" strokeWidth="2.5" strokeLinecap="round"
              strokeDasharray={`${seo.total * 0.975} 100`}
              className={scoreColor(seo.total)} />
          </svg>
          <span className={cn('absolute text-sm font-bold', scoreColor(seo.total))}>{seo.total}</span>
        </div>
        <div>
          <p className={cn('text-sm font-semibold', scoreColor(seo.total))}>{scoreLabel(seo.total)}</p>
          <p className="text-[10px] text-muted-foreground">Score SEO do video</p>
        </div>
      </div>
      <div className="space-y-1.5">
        {categories.map(c => {
          const pct = Math.round((c.score / c.max) * 100)
          return (
            <div key={c.label} className="flex items-center gap-2">
              <span className="w-16 text-[10px] font-medium text-muted-foreground">{c.label}</span>
              <div className="flex-1 h-1.5 rounded-full bg-border overflow-hidden">
                <div className={cn('h-full rounded-full transition-all', scoreBg(pct))} style={{ width: `${pct}%` }} />
              </div>
              <span className="w-8 text-right text-[10px] font-medium text-muted-foreground">{c.score}/{c.max}</span>
            </div>
          )
        })}
      </div>
      {allTips.length > 0 && (
        <div className="space-y-0.5">
          {allTips.slice(0, 3).map((tip, i) => (
            <p key={i} className="text-[10px] text-muted-foreground">
              <span className="text-yellow-500 mr-1">*</span>{tip}
            </p>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Helpers ──

function getSuggestedTime(): string {
  const day = new Date().getDay()
  return (day === 0 || day === 6) ? '10:00' : '14:00'
}

function formatDateBR(dateStr: string): string {
  const [y, m, d] = dateStr.split('-')
  return `${d}/${m}/${y}`
}

function addDays(dateStr: string, days: number): string {
  const d = new Date(dateStr + 'T12:00:00')
  d.setDate(d.getDate() + days)
  return d.toISOString().slice(0, 10)
}

// ── Per-video metadata type ──

interface VideoMeta {
  url: string
  filename: string
  title: string
  description: string
  tags: string
}

export function UploadDialog({
  open, onOpenChange, channelId,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  channelId: string
}) {
  // Mode toggle
  const [mode, setMode] = useState<'single' | 'schedule'>('single')

  // Single mode fields
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [tags, setTags] = useState('')
  const [privacy, setPrivacy] = useState('private')
  const [file, setFile] = useState<File | null>(null)
  const [progress, setProgress] = useState(0)
  const fileRef = useRef<HTMLInputElement>(null)
  const [source, setSource] = useState<'upload' | 'library'>('upload')

  // Library state
  const [selectedFolderId, setSelectedFolderId] = useState('')
  const [folderPickerOpen, setFolderPickerOpen] = useState(false)
  const [selectedVideoUrl, setSelectedVideoUrl] = useState('')

  // Schedule mode
  const [videoMetas, setVideoMetas] = useState<VideoMeta[]>([])
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null)
  const [schedulePrivacy, setSchedulePrivacy] = useState('private')
  const [startDate, setStartDate] = useState(() => {
    const d = new Date()
    d.setDate(d.getDate() + 1)
    return d.toISOString().slice(0, 10)
  })
  const [postTime, setPostTime] = useState(getSuggestedTime)

  const upload = useUploadYouTubeVideo()
  const uploadFromUrl = useUploadYouTubeVideoFromUrl()
  const scheduleVideos = useScheduleYouTubeVideos()
  const { data: mediaFolders } = useMediaFolders()
  const { data: aiStatus } = useOpenAIStatus()
  const { data: channelVideos } = useYouTubeVideos(channelId)
  const { data: tagPresets } = useYouTubeTagPresets()
  const generateYtMeta = useGenerateYtMeta()

  const selectedFolderData = mediaFolders?.find(f => f.id === selectedFolderId)

  const folderVideos = useMemo(() => {
    if (!selectedFolderData) return []
    return selectedFolderData.images.filter(img => img.mediaType === 'video')
  }, [selectedFolderData])

  const allSavedTags = useMemo(() => {
    if (!tagPresets) return []
    return [...new Set(Object.values(tagPresets).flat())]
  }, [tagPresets])

  // ── AI: single mode ──
  const handleGenerateYtMeta = async () => {
    if (!channelVideos || channelVideos.length === 0) {
      toast.error('Nenhum video de referencia no canal. Sincronize os videos primeiro.')
      return
    }
    try {
      const videos = channelVideos.slice(0, 15).map(v => ({ title: v.title, description: v.description, tags: v.tags }))
      const result = await generateYtMeta.mutateAsync({
        videos,
        savedTags: allSavedTags.length > 0 ? allSavedTags : undefined,
      })
      if (result.title) setTitle(result.title)
      if (result.description) setDescription(result.description)
      if (result.tags) setTags(result.tags)
      toast.success('Titulo, descricao e tags gerados!')
    } catch (err) {
      toast.error((err as Error).message || 'Erro ao gerar com IA')
    }
  }

  // ── AI: schedule mode — gera N sets unicos ──
  const handleGenerateAllMetas = async () => {
    if (!channelVideos || channelVideos.length === 0) {
      toast.error('Nenhum video de referencia no canal. Sincronize os videos primeiro.')
      return
    }
    if (videoMetas.length === 0) {
      toast.error('Selecione videos primeiro')
      return
    }
    try {
      const videos = channelVideos.slice(0, 15).map(v => ({ title: v.title, description: v.description, tags: v.tags }))
      const result = await generateYtMeta.mutateAsync({
        videos,
        savedTags: allSavedTags.length > 0 ? allSavedTags : undefined,
        count: videoMetas.length,
      })

      if (result.items && result.items.length > 0) {
        setVideoMetas(prev => prev.map((vm, i) => {
          const item = result.items![i % result.items!.length]
          return {
            ...vm,
            title: item.title || vm.title,
            description: item.description || vm.description,
            tags: item.tags || vm.tags,
          }
        }))
        toast.success(`${videoMetas.length} titulos, descricoes e tags gerados!`)
      } else if (result.title) {
        // Fallback: only 1 set returned — apply to first video
        setVideoMetas(prev => prev.map((vm, i) => i === 0 ? {
          ...vm,
          title: result.title || vm.title,
          description: result.description || vm.description,
          tags: result.tags || vm.tags,
        } : vm))
        toast.success('Titulo gerado para o primeiro video. Gere individualmente para os outros.')
      }
    } catch (err) {
      toast.error((err as Error).message || 'Erro ao gerar com IA')
    }
  }

  const isPending = upload.isPending || uploadFromUrl.isPending || scheduleVideos.isPending

  const reset = () => {
    setMode('single')
    setTitle('')
    setDescription('')
    setTags('')
    setPrivacy('private')
    setFile(null)
    setProgress(0)
    setSource('upload')
    setSelectedFolderId('')
    setFolderPickerOpen(false)
    setSelectedVideoUrl('')
    setVideoMetas([])
    setExpandedIdx(null)
    setSchedulePrivacy('private')
    const d = new Date()
    d.setDate(d.getDate() + 1)
    setStartDate(d.toISOString().slice(0, 10))
    setPostTime(getSuggestedTime())
  }

  const handleOpenChange = (v: boolean) => {
    if (v) reset()
    onOpenChange(v)
  }

  // ── Single mode upload ──
  const handleUpload = () => {
    if (!title.trim()) { toast.error('Informe o titulo do video'); return }

    const callbacks = {
      onSuccess: () => {
        toast.success('Video enviado com sucesso!')
        handleOpenChange(false)
      },
      onError: (err: Error) => {
        toast.error(`Erro no upload: ${err.message}`)
      },
    }

    if (source === 'library') {
      if (!selectedVideoUrl) { toast.error('Selecione um video da biblioteca'); return }
      uploadFromUrl.mutate({
        channelId,
        videoUrl: selectedVideoUrl,
        title: title.trim(),
        description: description.trim() || undefined,
        tags: tags.trim() || undefined,
        privacyStatus: privacy,
      }, callbacks)
    } else {
      if (!file) { toast.error('Selecione um arquivo de video'); return }
      upload.mutate({
        channelId,
        file,
        title: title.trim(),
        description: description.trim() || undefined,
        tags: tags.trim() || undefined,
        privacyStatus: privacy,
        onProgress: setProgress,
      }, callbacks)
    }
  }

  // ── Schedule mode ──
  const handleSchedule = () => {
    const missing = videoMetas.filter(vm => !vm.title.trim())
    if (missing.length > 0) {
      toast.error(`${missing.length} video(s) sem titulo. Preencha ou gere com IA.`)
      return
    }
    if (videoMetas.length === 0) { toast.error('Selecione ao menos um video'); return }
    if (!startDate) { toast.error('Selecione a data de inicio'); return }

    scheduleVideos.mutate({
      channelId,
      videos: videoMetas.map(vm => ({
        url: vm.url,
        title: vm.title.trim(),
        description: vm.description.trim(),
        tags: vm.tags.trim(),
      })),
      title: '', // not used, per-video titles
      description: '',
      tags: '',
      privacyStatus: schedulePrivacy,
      startDate,
      postTime,
    }, {
      onSuccess: (data) => {
        toast.success(`${data.count} videos agendados com sucesso!`)
        handleOpenChange(false)
      },
      onError: (err: Error) => {
        toast.error(`Erro ao agendar: ${err.message}`)
      },
    })
  }

  // ── Toggle video in schedule mode ──
  const toggleVideoSelection = (url: string, filename: string) => {
    setVideoMetas(prev => {
      const exists = prev.find(vm => vm.url === url)
      if (exists) return prev.filter(vm => vm.url !== url)
      return [...prev, { url, filename, title: '', description: '', tags: '' }]
    })
  }

  const selectAllVideos = () => {
    if (videoMetas.length === folderVideos.length) {
      setVideoMetas([])
    } else {
      setVideoMetas(folderVideos.map(v => ({
        url: v.url,
        filename: v.filename,
        title: '',
        description: '',
        tags: '',
      })))
    }
  }

  const updateVideoMeta = (idx: number, field: keyof VideoMeta, value: string) => {
    setVideoMetas(prev => prev.map((vm, i) => i === idx ? { ...vm, [field]: value } : vm))
  }

  const hasVideo = mode === 'single'
    ? (source === 'upload' ? !!file : !!selectedVideoUrl)
    : videoMetas.length > 0

  // Schedule preview
  const schedulePreview = useMemo(() => {
    if (videoMetas.length === 0 || !startDate) return null
    const endDate = addDays(startDate, videoMetas.length - 1)
    return {
      count: videoMetas.length,
      start: formatDateBR(startDate),
      end: formatDateBR(endDate),
      time: postTime,
    }
  }, [videoMetas.length, startDate, postTime])

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className={cn('max-h-[90vh] overflow-y-auto', mode === 'schedule' ? 'sm:max-w-2xl' : 'sm:max-w-lg')}>
        <DialogHeader>
          <DialogTitle>
            {mode === 'single' ? 'Upload de Video' : 'Agendar Videos'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'single' ? 'Envie um video para o YouTube' : 'Agende multiplos videos (1 por dia) com metadados unicos'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Mode toggle */}
          <div>
            <div className="flex gap-1 rounded-lg border border-border p-1">
              <button
                type="button"
                onClick={() => setMode('single')}
                className={cn(
                  'flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                  mode === 'single' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground',
                )}
              >
                <Upload className="h-3 w-3" /> Upload Unico
              </button>
              <button
                type="button"
                onClick={() => setMode('schedule')}
                className={cn(
                  'flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                  mode === 'schedule' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground',
                )}
              >
                <CalendarDays className="h-3 w-3" /> Agendar Diario
              </button>
            </div>
          </div>

          {/* ── SINGLE MODE ── */}
          {mode === 'single' && (
            <>
              <div>
                <Label>Origem do video</Label>
                <div className="mt-1.5 flex gap-1 rounded-lg border border-border p-1">
                  <button
                    type="button"
                    onClick={() => { setSource('upload'); setSelectedVideoUrl('') }}
                    className={cn(
                      'flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                      source === 'upload' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    <Upload className="h-3 w-3" /> Upload do PC
                  </button>
                  <button
                    type="button"
                    onClick={() => { setSource('library'); setFile(null); setProgress(0) }}
                    className={cn(
                      'flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                      source === 'library' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    <FolderOpen className="h-3 w-3" /> Biblioteca
                  </button>
                </div>
              </div>

              {source === 'upload' ? (
                <div>
                  <input ref={fileRef} type="file" accept="video/*" className="hidden" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
                  <Button variant="outline" className="w-full justify-start" onClick={() => fileRef.current?.click()}>
                    <Upload className="mr-2 h-4 w-4" />
                    {file ? file.name : 'Selecionar video...'}
                  </Button>
                  {file && <p className="mt-1 text-xs text-muted-foreground">{(file.size / (1024 * 1024)).toFixed(1)} MB</p>}
                </div>
              ) : (
                <div className="space-y-3">
                  <button type="button" onClick={() => setFolderPickerOpen(!folderPickerOpen)}
                    className={cn('flex h-9 w-full items-center justify-between rounded-md border px-3 text-xs transition-colors', folderPickerOpen ? 'border-primary' : 'border-input hover:border-primary/50')}>
                    {selectedFolderData ? (
                      <span className="flex items-center gap-1.5 min-w-0">
                        <FolderOpen className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                        <span className="truncate">{selectedFolderData.name}</span>
                        <span className="text-muted-foreground shrink-0">({folderVideos.length} video{folderVideos.length !== 1 && 's'})</span>
                      </span>
                    ) : (
                      <span className="text-muted-foreground">Selecione uma pasta...</span>
                    )}
                    <ChevronRight className={cn('h-3.5 w-3.5 text-muted-foreground shrink-0 transition-transform', folderPickerOpen && 'rotate-90')} />
                  </button>
                  {folderPickerOpen && mediaFolders && (
                    <FolderTreePicker folders={mediaFolders} selectedId={selectedFolderId}
                      onSelect={(id) => { setSelectedFolderId(id); setSelectedVideoUrl(''); setFolderPickerOpen(false) }} />
                  )}
                  {selectedFolderData && folderVideos.length > 0 && (
                    <div className="grid max-h-[200px] grid-cols-3 gap-1.5 overflow-y-auto sm:grid-cols-4">
                      {folderVideos.map(vid => {
                        const isSelected = selectedVideoUrl === vid.url
                        return (
                          <button key={vid.id} type="button" onClick={() => setSelectedVideoUrl(vid.url)}
                            className={cn('relative aspect-video overflow-hidden rounded-md border-2 transition-all', isSelected ? 'border-primary ring-2 ring-primary/30' : 'border-border hover:border-primary/40')}>
                            <video src={vid.url} className="h-full w-full object-cover" muted />
                            {isSelected && <div className="absolute inset-0 flex items-center justify-center bg-primary/20"><CheckCheck className="h-5 w-5 text-primary" /></div>}
                            <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-1 py-0.5"><p className="truncate text-[8px] text-white">{vid.filename}</p></div>
                          </button>
                        )
                      })}
                    </div>
                  )}
                  {selectedFolderData && folderVideos.length === 0 && <div className="py-4 text-center text-xs text-muted-foreground">Nenhum video nesta pasta</div>}
                  {selectedVideoUrl && (
                    <div className="flex items-center gap-2">
                      <video src={selectedVideoUrl} className="h-12 w-20 rounded-md border border-border object-cover" muted />
                      <div className="flex flex-col gap-0.5">
                        <Badge variant="outline" className="w-fit text-[8px]"><Video className="mr-1 h-2.5 w-2.5" /> Video selecionado</Badge>
                        <button type="button" onClick={() => setSelectedVideoUrl('')} className="text-[10px] text-destructive hover:underline">remover</button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Single mode fields */}
              <div>
                <div className="flex items-center justify-between">
                  <Label>Titulo</Label>
                  {aiStatus?.connected && (
                    <button type="button" onClick={handleGenerateYtMeta} disabled={generateYtMeta.isPending}
                      className="flex items-center gap-1 rounded px-2 py-0.5 text-[10px] font-medium text-muted-foreground hover:bg-primary/10 hover:text-primary disabled:opacity-50">
                      {generateYtMeta.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                      Gerar com IA
                    </button>
                  )}
                </div>
                <Input className="mt-1.5" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Titulo do video" />
              </div>
              <div>
                <Label>Descricao</Label>
                <Textarea className="mt-1.5" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Descricao do video (opcional)" />
              </div>
              <div>
                <Label>Tags (separadas por virgula)</Label>
                <Input className="mt-1.5" value={tags} onChange={(e) => setTags(e.target.value)} placeholder="tag1, tag2, tag3" />
              </div>
              <SeoScoreCard title={title} description={description} tags={tags} />
              <div>
                <Label>Privacidade</Label>
                <Select value={privacy} onValueChange={setPrivacy}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="public">Publico</SelectItem>
                    <SelectItem value="unlisted">Nao Listado</SelectItem>
                    <SelectItem value="private">Privado</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {upload.isPending && progress > 0 && (
                <div className="space-y-1">
                  <div className="flex justify-between text-xs text-muted-foreground"><span>Enviando...</span><span>{progress}%</span></div>
                  <div className="h-2 rounded-full bg-secondary overflow-hidden">
                    <div className="h-full bg-primary rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
                  </div>
                </div>
              )}
              {uploadFromUrl.isPending && (
                <div className="space-y-1">
                  <div className="text-xs text-muted-foreground">Enviando video da biblioteca para o YouTube...</div>
                  <div className="h-2 rounded-full bg-secondary overflow-hidden">
                    <div className="h-full bg-primary rounded-full animate-pulse w-2/3" />
                  </div>
                </div>
              )}
            </>
          )}

          {/* ── SCHEDULE MODE ── */}
          {mode === 'schedule' && (
            <>
              {/* Folder picker */}
              <div className="space-y-3">
                <Label>Selecione videos da biblioteca</Label>
                <button type="button" onClick={() => setFolderPickerOpen(!folderPickerOpen)}
                  className={cn('flex h-9 w-full items-center justify-between rounded-md border px-3 text-xs transition-colors', folderPickerOpen ? 'border-primary' : 'border-input hover:border-primary/50')}>
                  {selectedFolderData ? (
                    <span className="flex items-center gap-1.5 min-w-0">
                      <FolderOpen className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      <span className="truncate">{selectedFolderData.name}</span>
                      <span className="text-muted-foreground shrink-0">({folderVideos.length} video{folderVideos.length !== 1 && 's'})</span>
                    </span>
                  ) : (
                    <span className="text-muted-foreground">Selecione uma pasta...</span>
                  )}
                  <ChevronRight className={cn('h-3.5 w-3.5 text-muted-foreground shrink-0 transition-transform', folderPickerOpen && 'rotate-90')} />
                </button>

                {folderPickerOpen && mediaFolders && (
                  <FolderTreePicker folders={mediaFolders} selectedId={selectedFolderId}
                    onSelect={(id) => { setSelectedFolderId(id); setVideoMetas([]); setFolderPickerOpen(false) }} />
                )}

                {selectedFolderData && folderVideos.length > 0 && (
                  <>
                    <div className="flex items-center justify-between">
                      <p className="text-[10px] text-muted-foreground">
                        {videoMetas.length} video{videoMetas.length !== 1 && 's'} selecionado{videoMetas.length !== 1 && 's'}
                      </p>
                      <button type="button" onClick={selectAllVideos} className="text-[10px] text-primary hover:underline">
                        {videoMetas.length === folderVideos.length ? 'Desmarcar todos' : 'Selecionar todos'}
                      </button>
                    </div>
                    <div className="grid max-h-[200px] grid-cols-3 gap-1.5 overflow-y-auto sm:grid-cols-4">
                      {folderVideos.map(vid => {
                        const idx = videoMetas.findIndex(vm => vm.url === vid.url)
                        const isSelected = idx >= 0
                        return (
                          <button key={vid.id} type="button" onClick={() => toggleVideoSelection(vid.url, vid.filename)}
                            className={cn('relative aspect-video overflow-hidden rounded-md border-2 transition-all', isSelected ? 'border-primary ring-2 ring-primary/30' : 'border-border hover:border-primary/40')}>
                            <video src={vid.url} className="h-full w-full object-cover" muted />
                            {isSelected && (
                              <div className="absolute inset-0 flex items-center justify-center bg-primary/20">
                                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground">{idx + 1}</span>
                              </div>
                            )}
                            <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-1 py-0.5"><p className="truncate text-[8px] text-white">{vid.filename}</p></div>
                          </button>
                        )
                      })}
                    </div>
                  </>
                )}
                {selectedFolderData && folderVideos.length === 0 && <div className="py-4 text-center text-xs text-muted-foreground">Nenhum video nesta pasta</div>}
              </div>

              {/* Schedule config */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="flex items-center gap-1.5"><CalendarDays className="h-3 w-3" /> Data inicio</Label>
                  <Input type="date" className="mt-1.5" value={startDate} onChange={(e) => setStartDate(e.target.value)} min={new Date().toISOString().slice(0, 10)} />
                </div>
                <div>
                  <Label className="flex items-center gap-1.5">
                    <Clock className="h-3 w-3" /> Horario
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild><Info className="h-3 w-3 text-muted-foreground cursor-help" /></TooltipTrigger>
                        <TooltipContent side="top" className="max-w-[250px] text-xs">
                          Baseado em estudos de audiencia YT: 14-16h e 20-23h dias uteis, 10-12h fins de semana. Consulte YouTube Studio &gt; Publico para dados do seu canal.
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </Label>
                  <Input type="time" className="mt-1.5" value={postTime} onChange={(e) => setPostTime(e.target.value)} />
                </div>
              </div>

              {/* Privacy */}
              <div>
                <Label>Privacidade (todos os videos)</Label>
                <Select value={schedulePrivacy} onValueChange={setSchedulePrivacy}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="public">Publico</SelectItem>
                    <SelectItem value="unlisted">Nao Listado</SelectItem>
                    <SelectItem value="private">Privado</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Schedule preview */}
              {schedulePreview && (
                <div className="rounded-lg border border-primary/30 bg-primary/5 px-3 py-2">
                  <p className="text-xs text-primary font-medium">
                    {schedulePreview.count} video{schedulePreview.count !== 1 && 's'} sera{schedulePreview.count !== 1 && 'o'} publicado{schedulePreview.count !== 1 && 's'} de {schedulePreview.start} a {schedulePreview.end} as {schedulePreview.time}
                  </p>
                </div>
              )}

              {/* Generate all with AI button */}
              {videoMetas.length > 0 && aiStatus?.connected && (
                <Button variant="outline" size="sm" className="w-full gap-1.5" onClick={handleGenerateAllMetas} disabled={generateYtMeta.isPending}>
                  {generateYtMeta.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                  Gerar tudo com IA ({videoMetas.length} titulos + descricoes + tags unicos)
                </Button>
              )}

              {/* Per-video metadata cards */}
              {videoMetas.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-xs">Metadados por video</Label>
                  {videoMetas.map((vm, idx) => {
                    const isExpanded = expandedIdx === idx
                    const scheduledDate = startDate ? formatDateBR(addDays(startDate, idx)) : ''
                    return (
                      <div key={vm.url} className="rounded-lg border border-border bg-muted/20 overflow-hidden">
                        {/* Header — always visible */}
                        <button type="button" onClick={() => setExpandedIdx(isExpanded ? null : idx)}
                          className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-muted/40 transition-colors">
                          <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-[9px] font-bold text-primary-foreground">{idx + 1}</span>
                          <video src={vm.url} className="h-8 w-12 shrink-0 rounded border border-border object-cover" muted />
                          <div className="flex-1 min-w-0">
                            <p className="truncate text-[11px] font-medium">{vm.title || vm.filename}</p>
                            <p className="text-[9px] text-muted-foreground">{scheduledDate} as {postTime}</p>
                          </div>
                          {vm.title && <Badge variant="outline" className="shrink-0 text-[8px] text-emerald-500 border-emerald-500/30">OK</Badge>}
                          <ChevronDown className={cn('h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform', isExpanded && 'rotate-180')} />
                        </button>

                        {/* Expanded — editable fields */}
                        {isExpanded && (
                          <div className="space-y-2.5 border-t border-border px-3 py-2.5">
                            <div>
                              <Label className="text-[10px]">Titulo</Label>
                              <Input className="mt-1 h-8 text-xs" value={vm.title} onChange={(e) => updateVideoMeta(idx, 'title', e.target.value)} placeholder="Titulo unico deste video" />
                            </div>
                            <div>
                              <Label className="text-[10px]">Descricao</Label>
                              <Textarea className="mt-1 text-xs" rows={2} value={vm.description} onChange={(e) => updateVideoMeta(idx, 'description', e.target.value)} placeholder="Descricao (opcional)" />
                            </div>
                            <div>
                              <Label className="text-[10px]">Tags</Label>
                              <Input className="mt-1 h-8 text-xs" value={vm.tags} onChange={(e) => updateVideoMeta(idx, 'tags', e.target.value)} placeholder="tag1, tag2, tag3" />
                            </div>
                            <SeoScoreCard title={vm.title} description={vm.description} tags={vm.tags} />
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}

              {scheduleVideos.isPending && (
                <div className="space-y-1">
                  <div className="text-xs text-muted-foreground">Agendando videos...</div>
                  <div className="h-2 rounded-full bg-secondary overflow-hidden">
                    <div className="h-full bg-primary rounded-full animate-pulse w-2/3" />
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isPending}>Cancelar</Button>
          {mode === 'single' ? (
            <Button onClick={handleUpload} disabled={isPending || !hasVideo}>
              {isPending ? 'Enviando...' : 'Enviar Video'}
            </Button>
          ) : (
            <Button onClick={handleSchedule} disabled={isPending || !hasVideo}>
              {scheduleVideos.isPending ? 'Agendando...' : `Agendar ${videoMetas.length} Video${videoMetas.length !== 1 ? 's' : ''}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
